import { Alert, Button, Group, Stack, Tabs, Text } from "@mantine/core";
import { IconHelpHexagon } from "@tabler/icons-react";
import { type DateTime, Interval } from "luxon";
import { Activity, useEffect, useState } from "react";
import { type FieldErrors, type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ModalFooter } from "../../../../mantine/components/ModalFooter/ModalFooter";
import { TabWithHighlightAndError } from "../../../../mantine/components/TabWithHighlightAndError/TabWithHighlightAndError";
import { AlertPresets } from "../../../../mantine/mantinePresets";
import { useToast } from "../../../../mantine/notifications/useToast";
import {
  DirektvermarktungContractRemunerationType,
  Veraeusserungsform
} from "../../../../types/api.types";
import type {
  Direktvermarkter,
  OperatorListItem
} from "../../../../utils/backend-types";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { luxonDateTimeToBackendDateOrDateTime } from "../../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";
import { sortDateTime } from "../../../../utils/dates/sortDateTime";
import { buildFieldNameToLabelMap } from "../../../../utils/react-hook-form/buildFieldNameToLabelMap";
import { AnchorLink } from "../../../AnchorLink/AnchorLink";
import { FormFieldController } from "../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { FormDateRangeInput } from "../../../BuildingBlocks/Forms/FormFields/GroupedFields/FormDateRangeInput/FormDateRangeInput";
import { FormSection } from "../../../BuildingBlocks/Forms/FormSection/FormSection";
import { FormFieldSelect } from "../../../BuildingBlocks/Forms/MantineFormFields/fields/FormFieldSelect";
import { countErrorsForTab } from "../../../BuildingBlocks/Forms/utils/countErrorsForTab";
import { findFirstTabWithError } from "../../../BuildingBlocks/Forms/utils/findFirstTabWithError";
import { setErrorsFromResponseData } from "../../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { HistoryTab } from "../../../HistoryTab/HistoryTab";
import { useShouldShowStaffView } from "../../../StaffViewToggle/useShouldShowStaffView";
import type {
  DirektvermarktungContract,
  DirektvermarktungContractResponse,
  DirektvermarktungFormulaTemplatesResponse,
  FormulaVariablesResponse,
  PPAFixedPriceComponent,
  VermarktungAddons
} from "../../Direktvermarktung.types";
import {
  DIREKTVERMARKTER_ANONYMOUS_ID,
  DIREKTVERMARKTUNG_FORM_INPUT_DATA,
  DIREKTVERMARKTUNG_MARKETING_FORM_CHOICES,
  DV_CONTRACT_FORM_TABS,
  TABS
} from "./DirektvermarktungContractForm.constants";
import {
  type DirektvermarktungContractFixedPriceTableData,
  FIXED_PRICE_TABLE_ROW_LIMIT,
  FixedPriceTable,
  PERIOD_FORMAT
} from "./FixedPriceTable/FixedPriceTable";
import { FormulaFieldInfo } from "./FormulaFieldInfo/FormulaFieldInfo";
import { updatePPAFixedPriceComponentsOnDateRangeChange } from "./utils/updatePPAFixedPriceComponentsOnDateRangeChange";

interface DirektvermarktungContractFormProps {
  direktvermarktungContract?: Partial<DirektvermarktungContractResponse>;
  operators: Array<OperatorListItem>;
  revenueMonitoringOperators: Array<OperatorListItem>;
  paragraph6Operators: Array<OperatorListItem>;
  direktvermarkter: Array<Direktvermarkter>;
  formulaAllowedVariables: FormulaVariablesResponse;
  formulaTemplates?: DirektvermarktungFormulaTemplatesResponse;
  isSubmitting?: boolean;
  submissionError?: Error;
  templateContract?: DirektvermarktungContractResponse;
  onSubmit: SubmitHandler<DirektvermarktungContract>;
  onCancel: () => void;
}

const fieldNameToLabelMap = buildFieldNameToLabelMap(
  DIREKTVERMARKTUNG_FORM_INPUT_DATA
);

function DirektvermarktungContractForm({
  direktvermarktungContract,
  operators,
  revenueMonitoringOperators,
  paragraph6Operators,
  direktvermarkter,
  formulaAllowedVariables,
  formulaTemplates,
  isSubmitting = false,
  submissionError,
  templateContract,
  onSubmit,
  onCancel
}: DirektvermarktungContractFormProps) {
  const { showErrorToast, showToast } = useToast();
  const { t } = useTranslation();
  const { shouldShowStaffView } = useShouldShowStaffView();

  const [tab, setTab] = useState<string>(
    DV_CONTRACT_FORM_TABS.GENERAL_INFORMATION
  );

  const editMode = !!direktvermarktungContract?.id && !templateContract;
  const isCreateFromTemplate = !editMode && !!templateContract;

  const isAtLeastOneParagraph6Operator = paragraph6Operators.length > 0;
  const isAtLeastOneRevenueMonitoringOperators =
    revenueMonitoringOperators.length > 0;

  const activeAddons: Array<VermarktungAddons> = [];
  if (isAtLeastOneRevenueMonitoringOperators) {
    activeAddons.push("revenueMonitoring");
  }
  if (isAtLeastOneParagraph6Operator) {
    activeAddons.push("paragraph6");
  }

  const [showConfirmationDialogue, setShowConfirmationDialogue] =
    useState<boolean>(false);

  const preselectedFormValues = isAtLeastOneParagraph6Operator
    ? {
        redispatch_has_dedicated_entgelt: false,
        redispatch_has_dedicated_verguetung: false,
        abregelung_has_dedicated_entgelt: false,
        abregelung_has_dedicated_verguetung: false
      }
    : {};

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    setError,
    unregister,
    watch
  } = useForm<DirektvermarktungContract>({
    defaultValues: direktvermarktungContract ?? preselectedFormValues
  });

  const operatorChoices: Array<{ value: number; label: string }> =
    operators
      ?.sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
      ?.map((operator) => ({
        value: operator.id,
        label: operator.name
      })) || [];
  const hasGenerationShares =
    (direktvermarktungContract?.generation_shares?.length ?? 0) > 0;
  const operatorFieldInfo = {
    ...DIREKTVERMARKTUNG_FORM_INPUT_DATA.operator_id,
    data: operatorChoices,
    disabled: hasGenerationShares
  };

  const direktvermarkterChoices: Array<{ value: number; label: string }> =
    direktvermarkter
      ?.sort((a, b) => {
        if (a.id === DIREKTVERMARKTER_ANONYMOUS_ID) return -1;
        if (b.id === DIREKTVERMARKTER_ANONYMOUS_ID) return 1;
        return a.name.localeCompare(b.name);
      })
      .map((direktvermarkter) => ({
        value: direktvermarkter.id,
        label: direktvermarkter.name
      })) || [];
  const direktvermarkterFieldInfo = {
    ...DIREKTVERMARKTUNG_FORM_INPUT_DATA.direktvermarkter_id,
    data: direktvermarkterChoices
  };

  const selectedContractRemunerationType = watch("contract_remuneration_type");
  const showJahresmarktwertInfo =
    selectedContractRemunerationType ===
    DirektvermarktungContractRemunerationType.yearly_market_value_contract;

  const inferredVeraeusserungsform =
    direktvermarktungContract?.inferred_veraeusserungsform ?? null;
  const showDirektvermarkterSelection =
    inferredVeraeusserungsform !== Veraeusserungsform.feste_einspeiseverguetung;
  const showRemunerationSelection =
    inferredVeraeusserungsform !== Veraeusserungsform.feste_einspeiseverguetung;

  const selectedRemunerationType = watch("contract_remuneration_type");

  const formulaVerguetungFieldInfo = {
    ...DIREKTVERMARKTUNG_FORM_INPUT_DATA.formula_verguetung,
    variables: formulaAllowedVariables.einspeise_verguetung
  };
  const showFormulaVerguetungField =
    selectedRemunerationType ===
    DirektvermarktungContractRemunerationType.other_verguetung;

  const formulaEntgeltFieldInfo = {
    ...DIREKTVERMARKTUNG_FORM_INPUT_DATA.formula_entgelt,
    variables: formulaAllowedVariables.einspeise_entgelt,
    formulaTemplates: formulaTemplates?.["formula_entgelt"]
  };
  const showFormulaEntgeltField =
    inferredVeraeusserungsform !== Veraeusserungsform.feste_einspeiseverguetung;

  const showFixedPriceTable =
    showRemunerationSelection &&
    selectedRemunerationType ===
      DirektvermarktungContractRemunerationType.ppa_contract;
  const firstDate = watch("first_date");
  const lastDate = watch("last_date");
  const ppaFixedPriceComponents = watch("ppa_fixed_price_components");

  const showRedispatchDedicatedEntgeltFormula = watch(
    "redispatch_has_dedicated_entgelt"
  );
  const formulaRedispatchEntgeltFieldInfo = {
    ...DIREKTVERMARKTUNG_FORM_INPUT_DATA.formula_redispatch_entgelt,
    variables: formulaAllowedVariables.redispatch_entgelt
  };
  const showRedispatchDedicatedVerguetungFormula = watch(
    "redispatch_has_dedicated_verguetung"
  );
  const formulaRedispatchVerguetungFieldInfo = {
    ...DIREKTVERMARKTUNG_FORM_INPUT_DATA.formula_redispatch_verguetung,
    variables: formulaAllowedVariables.redispatch_verguetung
  };
  const showAbregelungDedicatedEntgeltFormula = watch(
    "abregelung_has_dedicated_entgelt"
  );
  const formulaAbregelungEntgeltFieldInfo = {
    ...DIREKTVERMARKTUNG_FORM_INPUT_DATA.formula_abregelung_entgelt,
    variables: formulaAllowedVariables.dva_entgelt
  };
  const showAbregelungDedicatedVerguetungFormula = watch(
    "abregelung_has_dedicated_verguetung"
  );
  const formulaAbregelungVerguetungFieldInfo = {
    ...DIREKTVERMARKTUNG_FORM_INPUT_DATA.formula_abregelung_verguetung,
    variables: formulaAllowedVariables.dva_verguetung
  };

  const selectedDirektvermarkterId = watch("direktvermarkter_id");

  const isValidNumberOfMonths = checkValidNumberOfMonths(firstDate, lastDate);

  useEffect(() => {
    if (submissionError) {
      setErrorsFromResponseData<DirektvermarktungContract>(
        showErrorToast,
        submissionError,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    }
  }, [setError, submissionError, t, watch, showErrorToast]);

  // Warn when the validity period is changed in edit mode, because the
  // inferred_veraeusserungsform may change as a result, which can either
  // make direktvermarkter/contract_remuneration_type fields obsolete (if the
  // new period falls under FE) or required (if the new period leaves FE).
  const originalFirstDate = direktvermarktungContract?.first_date;
  const originalLastDate = direktvermarktungContract?.last_date;
  const hasDirektvermarkter = !!direktvermarktungContract?.direktvermarkter_id;
  const periodChanged =
    firstDate !== originalFirstDate || lastDate !== originalLastDate;
  const hasNoInitialValidityPeriod =
    (!originalFirstDate && !originalLastDate) || isCreateFromTemplate;

  useEffect(() => {
    if (!periodChanged || hasNoInitialValidityPeriod) {
      return;
    }
    if (hasDirektvermarkter) {
      showToast({
        type: "warning",
        message:
          "Durch die Änderung des Gültigkeitszeitraums kann sich die Veräußerungsform des Erzeugers ändern. Falls die neue Periode unter 'Feste Einspeisevergütung' fällt, werden Vermarkter und Vergütung automatisch gelöscht."
      });
    } else {
      showToast({
        type: "warning",
        message:
          "Durch die Änderung des Gültigkeitszeitraums kann sich die Veräußerungsform des Erzeugers ändern. Falls die neue Periode nicht mehr unter 'Feste Einspeisevergütung' fällt, müssen Direktvermarkter und Vergütungsart gesetzt werden."
      });
    }
  }, [
    periodChanged,
    hasNoInitialValidityPeriod,
    hasDirektvermarkter,
    showToast
  ]);

  useEffect(() => {
    if (!showDirektvermarkterSelection) {
      setValue("direktvermarkter_id", null);
    }
  }, [showDirektvermarkterSelection, setValue]);
  useEffect(() => {
    if (!showRemunerationSelection) {
      setValue("contract_remuneration_type", null);
    }
  }, [showRemunerationSelection, setValue]);

  useEffect(() => {
    if (!showFormulaEntgeltField) {
      setValue("formula_entgelt", undefined);
    }
  }, [showFormulaEntgeltField, setValue]);

  useEffect(() => {
    if (showFixedPriceTable) {
      register("ppa_fixed_price_components");
    } else {
      unregister("ppa_fixed_price_components");
    }
  }, [showFixedPriceTable, register, unregister]);

  useEffect(() => {
    if (!showFixedPriceTable || !isValidNumberOfMonths) {
      return;
    }

    const updatedComponents = updatePPAFixedPriceComponentsOnDateRangeChange(
      ppaFixedPriceComponents,
      firstDate,
      lastDate
    );
    if (updatedComponents) {
      setValue(
        "ppa_fixed_price_components",
        updatedComponents as PPAFixedPriceComponent[]
      );
    }
  }, [
    firstDate,
    lastDate,
    isValidNumberOfMonths,
    ppaFixedPriceComponents,
    showFixedPriceTable,
    setValue
  ]);

  useEffect(() => {
    if (!showRedispatchDedicatedEntgeltFormula) {
      setValue("formula_redispatch_entgelt", undefined);
    }
  }, [showRedispatchDedicatedEntgeltFormula, setValue]);

  useEffect(() => {
    if (!showRedispatchDedicatedVerguetungFormula) {
      setValue("formula_redispatch_verguetung", undefined);
    }
  }, [showRedispatchDedicatedVerguetungFormula, setValue]);

  useEffect(() => {
    if (!showAbregelungDedicatedEntgeltFormula) {
      setValue("formula_abregelung_entgelt", undefined);
    }
  }, [showAbregelungDedicatedEntgeltFormula, setValue]);

  useEffect(() => {
    if (!showAbregelungDedicatedVerguetungFormula) {
      setValue("formula_abregelung_verguetung", undefined);
    }
  }, [showAbregelungDedicatedVerguetungFormula, setValue]);

  function handleRevenueChange(
    newRevenue: number | undefined,
    startDate: DateTime,
    endDate: DateTime
  ) {
    const backendStartDate = luxonDateTimeToBackendDateOrDateTime(
      startDate,
      "ISO 8601"
    );
    const backendEndDate = luxonDateTimeToBackendDateOrDateTime(
      endDate,
      "ISO 8601"
    );
    const updatedComponent: PPAFixedPriceComponent = {
      end_date: backendEndDate,
      revenue: newRevenue || null,
      start_date: backendStartDate
    };
    const fixedPriceComponents = ppaFixedPriceComponents ?? [];
    const updatedComponents = fixedPriceComponents.map((component) => {
      if (
        component.end_date === updatedComponent.end_date &&
        component.start_date === updatedComponent.start_date
      ) {
        return updatedComponent;
      }

      return component;
    });

    setValue("ppa_fixed_price_components", updatedComponents);
  }

  function handleAllRevenuesChange(newRevenue: number | undefined) {
    const fixedPriceComponents = ppaFixedPriceComponents ?? [];
    const updatedComponents = fixedPriceComponents.map((component) => ({
      ...component,
      revenue: newRevenue || null
    }));

    setValue("ppa_fixed_price_components", updatedComponents);
  }

  const fixedPriceTableData =
    ppaFixedPriceComponents
      ?.sort((a, b) =>
        sortDateTime(
          backendDateOrDateTimeToLuxonDateTime(a.start_date),
          backendDateOrDateTimeToLuxonDateTime(b.start_date)
        )
      )
      .map<DirektvermarktungContractFixedPriceTableData>((component) => {
        const startDateTime = backendDateOrDateTimeToLuxonDateTime(
          component.start_date
        );
        const period = startDateTime.toFormat(PERIOD_FORMAT);
        const revenue = component.revenue ?? undefined;

        return {
          period,
          revenue
        };
      }) ?? [];

  const disableDirektvermarkterSelection = editMode && !shouldShowStaffView;

  const tabsFilteredForAddons = TABS.filter(
    (tab) =>
      !tab.showForAddons ||
      tab.showForAddons.some((addon) => activeAddons.includes(addon))
  );

  const isRedispatchAusfallErmittlungDefaultOpen =
    !direktvermarktungContract?.redispatch_status_duldungsfall &&
    !direktvermarktungContract?.redispatch_bilanzierungsmodell &&
    !direktvermarktungContract?.redispatch_tr_abrechnungsmodell;

  const isAbregelungAusfallErmittlungDefaultOpen =
    !direktvermarktungContract?.abregelung_tr_abrechnungsmodell;

  useEffect(() => {
    if (!isSubmitting) {
      setShowConfirmationDialogue(false);
    }
  }, [isSubmitting]);

  function onError(errors: FieldErrors<DirektvermarktungContract>) {
    setShowConfirmationDialogue(false);
    const tabOrder = Object.values(DV_CONTRACT_FORM_TABS);
    const firstTabWithError = findFirstTabWithError(
      errors,
      DIREKTVERMARKTUNG_FORM_INPUT_DATA,
      tabOrder
    );

    if (firstTabWithError) {
      setTab(firstTabWithError);
    }
  }

  const vermarkterChanged =
    showDirektvermarkterSelection &&
    !!selectedDirektvermarkterId &&
    ((isCreateFromTemplate &&
      selectedDirektvermarkterId !== templateContract?.direktvermarkter_id) ||
      (editMode &&
        selectedDirektvermarkterId !==
          direktvermarktungContract?.direktvermarkter_id));

  return (
    <form
      className="DirektvermarktungContractForm"
      noValidate
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Activity mode={showConfirmationDialogue ? "hidden" : "visible"}>
        {editMode && (
          <Alert
            color={AlertPresets.info.color}
            icon={AlertPresets.info.icon}
            mb="md"
            title="Änderungen an laufenden Verträgen"
          >
            Sie können Angaben im aktuell geöffneten Vertrag jederzeit
            korrigieren, solange es nur die laufende Vertragsperiode betrifft.
            Ändern sich die Konditionen grundlegend, legen Sie bitte einen
            <strong> neuen Vertrag</strong> an. Passen Sie bei Bedarf zuerst die
            Laufzeit des aktuellen Vertrags an – so bleiben die Historie und
            Nachvollziehbarkeit erhalten.
          </Alert>
        )}
        {!!isCreateFromTemplate && (
          <Alert
            bd="1px solid var(--mantine-color-accent1-7)"
            color="accent1"
            mb="spacingXl"
          >
            <Group align="flex-start" gap="sm" wrap="nowrap">
              <IconHelpHexagon
                color="var(--mantine-color-accent1-7)"
                size="1.4em"
                style={{ flexShrink: 0 }}
              />
              Bitte überprüfen Sie die Informationen, die aus dem Vertrag „
              {templateContract?.name}“ ({templateContract?.first_date} -{" "}
              {templateContract?.last_date}) übernommen wurden. Passen Sie die
              Variablen, die sich ändern, entsprechend an.
            </Group>
          </Alert>
        )}
        <Tabs
          value={tab}
          onChange={(newTab) =>
            setTab(newTab as keyof typeof DV_CONTRACT_FORM_TABS)
          }
        >
          <Tabs.List>
            {tabsFilteredForAddons
              .filter((tab) => editMode || !tab.hiddenInCreateMode)
              .map((tab) => (
                <TabWithHighlightAndError
                  key={tab.value}
                  numErrors={countErrorsForTab(
                    tab.value,
                    DIREKTVERMARKTUNG_FORM_INPUT_DATA,
                    errors
                  )}
                  value={tab.value}
                >
                  {tab.label}
                </TabWithHighlightAndError>
              ))}
          </Tabs.List>
          <Tabs.Panel value={DV_CONTRACT_FORM_TABS.GENERAL_INFORMATION}>
            <section>
              <FormFieldController
                control={control}
                data={DIREKTVERMARKTUNG_FORM_INPUT_DATA.name}
                error={errors.name}
              />
              <FormDateRangeInput
                endDateFieldName="last_date"
                errors={errors}
                label="Gültigkeit der Vermarktungsinformationen"
                register={register}
                registerOptions={DIREKTVERMARKTUNG_FORM_INPUT_DATA}
                setValue={setValue}
                startDateFieldName="first_date"
                unregister={unregister}
                watch={watch}
              />
              <FormFieldController
                control={control}
                data={operatorFieldInfo}
                error={errors.operator_id}
              />
              <FormFieldSelect
                data={DIREKTVERMARKTUNG_MARKETING_FORM_CHOICES}
                disabled
                helpText={`Das Feld „Veräußerungsform" wurde umgezogen und wird nun direkt am Erzeuger gepflegt – unter „Daten zur EEG-Anlage". Da sie zeitlich veränderbar ist, kann sie dort auch für verschiedene Zeiträume hinterlegt werden. Im Vertrag wird die Veräußerungsform automatisch angezeigt, sobald die entsprechenden Erzeuger zugeordnet sind.`}
                label="Veräußerungsform"
                mb="sm"
                placeholder={
                  hasGenerationShares
                    ? "Keiner der zugeordneten Erzeuger ist im Vertragszeitraum in Betrieb"
                    : "Wird abgeleitet, sobald Erzeuger dem Vertrag zugeordnet sind"
                }
                type="select"
                value={inferredVeraeusserungsform}
              />
              {showDirektvermarkterSelection && (
                <FormFieldController
                  control={control}
                  data={{
                    ...direktvermarkterFieldInfo,
                    disabled: disableDirektvermarkterSelection,
                    infoText: editMode
                      ? "Der Vermarkter ist bereits für den weiteren Prozess der Ausfallarbeit im Erlösmonitoring eingebunden und kann nicht mehr angepasst werden."
                      : undefined
                  }}
                  error={errors.direktvermarkter_id}
                />
              )}
              {vermarkterChanged && (
                <Alert
                  {...AlertPresets.info}
                  bd="1px solid info"
                  mb="spacingSm"
                >
                  Sie haben einen anderen Vermarkter ausgewählt. Bitte prüfen
                  Sie den{" "}
                  <AnchorLink href="https://support.node.energy/knowledge/dv-liste">
                    Leistungsumfang
                  </AnchorLink>{" "}
                  und die Liste der Vermarkter, die opti.node unterstützt.
                </Alert>
              )}
              {showRemunerationSelection && (
                <FormFieldController
                  control={control}
                  data={
                    DIREKTVERMARKTUNG_FORM_INPUT_DATA.contract_remuneration_type
                  }
                  error={errors.contract_remuneration_type}
                />
              )}
              {showJahresmarktwertInfo && (
                <Alert
                  color={AlertPresets.warning.color}
                  icon={AlertPresets.warning.icon}
                >
                  Bitte beachten Sie, dass es hier nicht um die Angabe Ihrer
                  Förderung geht. Sofern Sie mit Ihrem Vermarkter auch die
                  Vergütung per Jahresmarktwert vereinbart haben, können Sie
                  fortfahren.
                </Alert>
              )}
              {showFixedPriceTable && isValidNumberOfMonths && (
                <FixedPriceTable
                  data={fixedPriceTableData}
                  onAllRevenuesChange={handleAllRevenuesChange}
                  onRevenueChange={handleRevenueChange}
                />
              )}
              {showFixedPriceTable && !isValidNumberOfMonths && (
                <Alert
                  color={AlertPresets.error.color}
                  icon={AlertPresets.error.icon}
                >
                  Der ausgewählte Zeitraum für die Gültigkeit der
                  Vermarktungsinformationen ist zu groß, um einzelne Werte für
                  den Tatsächlichen Erlös einzutragen. Bitte wählen Sie einen
                  kleineren Zeitraum aus.
                </Alert>
              )}
              {showFormulaVerguetungField && (
                <FormFieldController
                  control={control}
                  data={{
                    ...formulaVerguetungFieldInfo,
                    topArea: <FormulaFieldInfo />
                  }}
                  error={errors.formula_verguetung}
                />
              )}
              {showFormulaEntgeltField && (
                <FormFieldController
                  control={control}
                  data={{
                    ...formulaEntgeltFieldInfo,
                    topArea: <FormulaFieldInfo />
                  }}
                  error={errors.formula_entgelt}
                />
              )}
            </section>
          </Tabs.Panel>
          <Tabs.Panel value={DV_CONTRACT_FORM_TABS.REDISPATCH}>
            <FormSection
              defaultOpen={
                isCreateFromTemplate || isRedispatchAusfallErmittlungDefaultOpen
              }
              fieldNamesForMissingFields={[
                DIREKTVERMARKTUNG_FORM_INPUT_DATA.redispatch_status_duldungsfall
                  .name,
                DIREKTVERMARKTUNG_FORM_INPUT_DATA.redispatch_bilanzierungsmodell
                  .name,
                DIREKTVERMARKTUNG_FORM_INPUT_DATA
                  .redispatch_tr_abrechnungsmodell.name
              ]}
              isCollapsible
              label="Regelung zur Ermittlung der Ausfallarbeit"
              mb="md"
            >
              <FormFieldController
                control={control}
                data={
                  DIREKTVERMARKTUNG_FORM_INPUT_DATA.redispatch_status_duldungsfall
                }
                error={errors.redispatch_status_duldungsfall}
              />
              <FormFieldController
                control={control}
                data={
                  DIREKTVERMARKTUNG_FORM_INPUT_DATA.redispatch_bilanzierungsmodell
                }
                error={errors.redispatch_bilanzierungsmodell}
              />
              <FormFieldController
                control={control}
                data={
                  DIREKTVERMARKTUNG_FORM_INPUT_DATA.redispatch_tr_abrechnungsmodell
                }
                error={errors.redispatch_tr_abrechnungsmodell}
              />
            </FormSection>
            <FormSection
              defaultOpen={isCreateFromTemplate}
              fieldNamesForMissingFields={[
                DIREKTVERMARKTUNG_FORM_INPUT_DATA
                  .redispatch_has_dedicated_entgelt.name,
                DIREKTVERMARKTUNG_FORM_INPUT_DATA
                  .redispatch_has_dedicated_verguetung.name
              ]}
              isCollapsible
              label="Entgelt und Vergütung"
            >
              <FormFieldController
                control={control}
                data={
                  DIREKTVERMARKTUNG_FORM_INPUT_DATA.redispatch_has_dedicated_entgelt
                }
                error={errors.redispatch_has_dedicated_entgelt}
              />
              {showRedispatchDedicatedEntgeltFormula && (
                <FormFieldController
                  control={control}
                  data={formulaRedispatchEntgeltFieldInfo}
                  error={errors.formula_redispatch_entgelt}
                />
              )}
              <FormFieldController
                control={control}
                data={
                  DIREKTVERMARKTUNG_FORM_INPUT_DATA.redispatch_has_dedicated_verguetung
                }
                error={errors.redispatch_has_dedicated_verguetung}
              />
              {showRedispatchDedicatedVerguetungFormula && (
                <FormFieldController
                  control={control}
                  data={formulaRedispatchVerguetungFieldInfo}
                  error={errors.formula_redispatch_verguetung}
                />
              )}
            </FormSection>
          </Tabs.Panel>
          <Tabs.Panel value={DV_CONTRACT_FORM_TABS.ABREGELUNG}>
            <FormSection
              defaultOpen={
                isCreateFromTemplate || isAbregelungAusfallErmittlungDefaultOpen
              }
              fieldNamesForMissingFields={[
                DIREKTVERMARKTUNG_FORM_INPUT_DATA
                  .abregelung_tr_abrechnungsmodell.name
              ]}
              isCollapsible
              label="Regelung zur Ermittlung der Ausfallarbeit"
              mb="md"
            >
              {inferredVeraeusserungsform === null && (
                <Alert
                  color={AlertPresets.info.color}
                  icon={AlertPresets.info.icon}
                  mb="xs"
                >
                  Sofern Sie Erzeuger mit der Veräußerungsform{" "}
                  <strong>feste Einspeisevergütung</strong> verwalten wollen,
                  tragen Sie hier gerne Pauschal ein.
                </Alert>
              )}
              <FormFieldController
                control={control}
                data={
                  DIREKTVERMARKTUNG_FORM_INPUT_DATA.abregelung_tr_abrechnungsmodell
                }
                error={errors.abregelung_tr_abrechnungsmodell}
              />
            </FormSection>
            <FormSection
              defaultOpen={isCreateFromTemplate}
              fieldNamesForMissingFields={[
                DIREKTVERMARKTUNG_FORM_INPUT_DATA
                  .abregelung_has_dedicated_entgelt.name,
                DIREKTVERMARKTUNG_FORM_INPUT_DATA
                  .abregelung_has_dedicated_verguetung.name
              ]}
              isCollapsible
              label="Entgelt und Vergütung"
            >
              <FormFieldController
                control={control}
                data={
                  DIREKTVERMARKTUNG_FORM_INPUT_DATA.abregelung_has_dedicated_entgelt
                }
                error={errors.abregelung_has_dedicated_entgelt}
              />
              {showAbregelungDedicatedEntgeltFormula && (
                <FormFieldController
                  control={control}
                  data={formulaAbregelungEntgeltFieldInfo}
                  error={errors.formula_abregelung_entgelt}
                />
              )}
              <FormFieldController
                control={control}
                data={
                  DIREKTVERMARKTUNG_FORM_INPUT_DATA.abregelung_has_dedicated_verguetung
                }
                error={errors.abregelung_has_dedicated_verguetung}
              />
              {showAbregelungDedicatedVerguetungFormula && (
                <FormFieldController
                  control={control}
                  data={formulaAbregelungVerguetungFieldInfo}
                  error={errors.formula_abregelung_verguetung}
                />
              )}
            </FormSection>
          </Tabs.Panel>
          <Tabs.Panel value={DV_CONTRACT_FORM_TABS.HISTORY}>
            {direktvermarktungContract?.id && (
              <HistoryTab
                entityId={direktvermarktungContract.id}
                entityType="direktvermarktungContract"
                fieldNameToLabelMap={fieldNameToLabelMap}
              />
            )}
          </Tabs.Panel>
        </Tabs>
      </Activity>
      <Activity mode={showConfirmationDialogue ? "visible" : "hidden"}>
        <Stack my="xl">
          <Text>
            Sie haben die Vertragsdaten aus dem Vertrag{" "}
            <Text fw={600} span>
              „{templateContract?.name}“
            </Text>{" "}
            ({templateContract?.first_date} - {templateContract?.last_date})
            übernommen.
          </Text>

          <Text>
            Bitte stellen Sie sicher, dass die Abschnitte{" "}
            <Text fw={600} span>
              „Allgemeine Daten“
            </Text>
            ,{" "}
            <Text fw={600} span>
              „Redispatch“
            </Text>{" "}
            und{" "}
            <Text fw={600} span>
              „Abregelung“
            </Text>{" "}
            gründlich gelesen wurden und zum neuen Vertrag passen.
          </Text>

          <Text>
            Fehlerhafte Eingaben können die Erlösmonitoring-Daten beeinflussen.
          </Text>
        </Stack>
      </Activity>
      <ModalFooter justify="space-between" my="md">
        {showConfirmationDialogue ? (
          <Button
            disabled={isSubmitting}
            variant="default"
            onClick={() => setShowConfirmationDialogue(false)}
          >
            Zurück
          </Button>
        ) : (
          <Button disabled={isSubmitting} variant="default" onClick={onCancel}>
            Abbrechen
          </Button>
        )}
        {isCreateFromTemplate ? (
          <Button
            color="primary"
            loading={isSubmitting}
            onClick={
              !showConfirmationDialogue
                ? () => setShowConfirmationDialogue(true)
                : handleSubmit(onSubmit, onError)
            }
          >
            Übernehmen & Vertrag anlegen
          </Button>
        ) : (
          <Button color="primary" loading={isSubmitting} type="submit">
            Speichern
          </Button>
        )}
      </ModalFooter>
    </form>
  );
}

function checkValidNumberOfMonths(
  firstSelectedDate: string,
  lastSelectedDate: string
) {
  const firstDate = backendDateOrDateTimeToLuxonDateTime(firstSelectedDate);
  const lastDate = backendDateOrDateTimeToLuxonDateTime(lastSelectedDate);
  return (
    Interval.fromDateTimes(firstDate, lastDate).count("months") <=
    FIXED_PRICE_TABLE_ROW_LIMIT
  );
}

export { DirektvermarktungContractForm, DirektvermarktungContractFormProps };
